import React from 'react';
import { Helmet } from 'react-helmet-async';
import * as FaIcons from 'react-icons/fa';
import SafeIcon from '../common/SafeIcon';

const {
  FaCheckCircle,
  FaPalette,
  FaRocket,
  FaShieldAlt
} = FaIcons;

const articleDates = {
  published: '2026-03-07',
  modified: '2026-03-07',
  publishedLabel: '2026 年 3 月 7 日',
  modifiedLabel: '2026 年 3 月 7 日'
};

const features = [
  {
    id: 'privacy',
    icon: FaShieldAlt,
    color: 'text-primary',
    border: 'border-l-primary',
    title: 'QR星球如何保障您的隱私?',
    description: '所有 QR Code 均在您的瀏覽器本地生成，數據絕不上傳伺服器，保障 100% 隱私安全。'
  },
  {
    id: 'branding',
    icon: FaPalette,
    color: 'text-accent',
    border: 'border-l-accent',
    title: '如何製作符合品牌風格的 QR Code?',
    description: '內建「魔法調色」引擎，一鍵生成符合品牌美感的配色與圓角設計。'
  },
  {
    id: 'validity',
    icon: FaRocket,
    color: 'text-secondary',
    border: 'border-l-secondary',
    title: 'QR星球生成的 QR Code 會永久有效嗎?',
    description: '生成的靜態 QR Code 永久可用，無掃描次數限制，下載即可投入商用。'
  },
  {
    id: 'formats',
    icon: FaCheckCircle,
    color: 'text-green-500',
    border: 'border-l-green-500',
    title: 'QR星球支援哪些 QR Code 格式?',
    description: '無論是網址、PDF、WhatsApp 訊息還是聯絡資訊，QR星球都能輕鬆轉化。'
  }
];

const organization = {
  '@type': 'Organization',
  '@id': 'https://homy.hk/#organization',
  name: 'Homy Tech',
  url: 'https://homy.hk/',
  contactPoint: {
    '@type': 'ContactPoint',
    url: 'https://homy.hk/',
    contactType: 'customer support',
    availableLanguage: ['zh-Hant', 'en']
  }
};

const structuredData = {
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'Article',
      '@id': 'https://qr.homy.hk/#article',
      headline: '為什麼選擇 QR星球製作免費 QR Code?',
      description: '2026 年值得信賴的免費二維碼生成解決方案',
      url: 'https://qr.homy.hk/',
      mainEntityOfPage: 'https://qr.homy.hk/',
      inLanguage: 'zh-Hant',
      datePublished: articleDates.published,
      dateModified: articleDates.modified,
      author: organization,
      publisher: {
        '@id': 'https://homy.hk/#organization'
      }
    },
    {
      '@type': 'FAQPage',
      '@id': 'https://qr.homy.hk/#faq',
      url: 'https://qr.homy.hk/#faq',
      inLanguage: 'zh-Hant',
      mainEntity: features.map((feature) => ({
        '@type': 'Question',
        name: feature.title,
        acceptedAnswer: {
          '@type': 'Answer',
          text: feature.description
        }
      }))
    },
    {
      '@type': 'AboutPage',
      '@id': 'https://qr.homy.hk/#about',
      name: '關於 QR星球',
      url: 'https://qr.homy.hk/#about',
      inLanguage: 'zh-Hant',
      about: {
        '@id': 'https://qr.homy.hk/#application'
      },
      publisher: {
        '@id': 'https://homy.hk/#organization'
      }
    },
    {
      '@type': 'ContactPage',
      '@id': 'https://qr.homy.hk/#contact',
      name: '聯絡 QR星球',
      url: 'https://qr.homy.hk/#contact',
      inLanguage: 'zh-Hant',
      mainEntity: organization
    }
  ]
};

const FeatureCard = ({ feature }) => (
  <section
    className={`glass-card p-6 flex gap-4 border-l-4 ${feature.border}`}
    aria-labelledby={`faq-${feature.id}`}
  >
    <div className={`${feature.color} text-2xl mt-1`}>
      <SafeIcon icon={feature.icon} aria-hidden="true" />
    </div>
    <div>
      <h3
        id={`faq-${feature.id}`}
        className="text-xl font-semibold text-white mb-2"
      >
        {feature.title}
      </h3>
      <p className="text-sm text-slate-400">
        {feature.description}
      </p>
    </div>
  </section>
);

const SEOContent = () => {
  return (
    <>
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      </Helmet>

      <article
        className="w-full max-w-4xl mx-auto mt-16 px-4"
        itemScope
        itemType="https://schema.org/Article"
        aria-labelledby="seo-content-heading"
      >
        <meta itemProp="inLanguage" content="zh-Hant" />
        <meta itemProp="author" content="Homy Tech" />
        <meta itemProp="mainEntityOfPage" content="https://qr.homy.hk/" />

        <header className="text-center mb-12">
          <h2
            id="seo-content-heading"
            itemProp="headline"
            className="text-3xl font-bold text-white mb-4"
          >
            為什麼選擇 QR星球製作免費 QR Code?
          </h2>
          <p itemProp="description" className="text-slate-400">
            2026 年值得信賴的免費二維碼生成解決方案
          </p>

          <p
            className="mt-4 flex flex-wrap items-center justify-center gap-x-2 gap-y-1 text-xs text-slate-500"
            aria-label="文章發布及最後更新日期"
          >
            <span>
              發布日期：
              <time
                itemProp="datePublished"
                dateTime={articleDates.published}
              >
                {articleDates.publishedLabel}
              </time>
            </span>
            <span aria-hidden="true">·</span>
            <span>
              最後更新：
              <time
                itemProp="dateModified"
                dateTime={articleDates.modified}
              >
                {articleDates.modifiedLabel}
              </time>
            </span>
          </p>
        </header>

        <section
          id="faq"
          aria-labelledby="faq-heading"
          itemProp="articleBody"
          className="scroll-mt-8"
        >
          <h2 id="faq-heading" className="sr-only">
            使用 QR星球前有哪些常見問題?
          </h2>
          <div className="grid md:grid-cols-2 gap-8">
            {features.map((feature) => (
              <FeatureCard key={feature.id} feature={feature} />
            ))}
          </div>
        </section>

        <div className="mt-12 text-[10px] text-slate-800 select-none">
          QR Code Generator 2026, Free Online QR Creator, Homy Tech Solutions,
          Privacy QR Maker, Custom QR Design Hong Kong.
        </div>
      </article>
    </>
  );
};

export default SEOContent;